import { IUserAgentInfo, IVersionParts } from '@Services/HttpService/HttpService.types';
import {
    OSName,
    BrowserName,
    EngineName,
    DeviceType,
    Vendor,
    DeviceModel,
    DeviceRegexRule,
    OSRegexRule,
    BrowserRegexRule,
    EngineRegexRule
} from './Logger.types';

type NamedMatchResult<NameType extends string> = {
    name: NameType;
    version: string;
};

type DeviceMatchResult = {
    type: DeviceType;
    vendor: string | null;
    model: string | null;
};

const OS_RULES: OSRegexRule[] = [
    { regex: /windows nt (\d+[._]?\d*)/i, name: OSName.Windows, versionGroup: 1 },
    { regex: /android (\d+[._]?\d*)/i, name: OSName.Android, versionGroup: 1 },
    { regex: /iphone os (\d+[_.]\d+(_\d+)?)/i, name: OSName.iOS, versionGroup: 1 },
    { regex: /ipad; cpu os (\d+[_.]\d+(_\d+)?)/i, name: OSName.iOS, versionGroup: 1 },
    { regex: /mac os x (\d+[_.]\d+(_\d+)?)/i, name: OSName.macOS, versionGroup: 1 },
    { regex: /linux/i, name: OSName.Linux }
];

const DEVICE_RULES: DeviceRegexRule[] = [
    { regex: /iPhone/i, type: DeviceType.Mobile, vendor: Vendor.Apple, model: DeviceModel.iPhone },
    { regex: /iPad/i, type: DeviceType.Tablet, vendor: Vendor.Apple, model: DeviceModel.iPad },
    { regex: /Macintosh/i, type: DeviceType.Desktop, vendor: Vendor.Apple },
    { regex: /Android [\d._]+; (SM-[A-Z0-9]+)/i, type: DeviceType.Mobile, vendor: Vendor.Samsung, modelGroup: 1 },
    { regex: /Android [\d._]+; (Pixel \d+\w*)/i, type: DeviceType.Mobile, vendor: Vendor.Google, modelGroup: 1 },
    { regex: /Android [\d._]+; (moto[^\s;]+)/i, type: DeviceType.Mobile, vendor: Vendor.Motorola, modelGroup: 1 },
    { regex: /Android [\d._]+; ([A-Z]{2,}-[A-Z0-9]+)/i, type: DeviceType.Mobile, vendor: Vendor.Huawei, modelGroup: 1 },
    { regex: /Android [\d._]+; (Redmi [\w\d]+)/i, type: DeviceType.Mobile, vendor: Vendor.Xiaomi, modelGroup: 1 },
    { regex: /Android [\d._]+; (Xiaomi [\w\d]+)/i, type: DeviceType.Mobile, vendor: Vendor.Xiaomi, modelGroup: 1 },
    { regex: /mobile/i, type: DeviceType.Mobile },
    { regex: /tablet/i, type: DeviceType.Tablet },
    { regex: /.*/i, type: DeviceType.Desktop }
];

const BROWSER_RULES: BrowserRegexRule[] = [
    { regex: /(firefox)\/(\d+(\.\d+){0,2})/i, name: BrowserName.Firefox, versionGroup: 2 },
    { regex: /(chrome)\/(\d+(\.\d+){0,2})/i, name: BrowserName.Chrome, versionGroup: 2 },
    { regex: /(safari)\/(\d+(\.\d+){0,2})/i, name: BrowserName.Safari, versionGroup: 2 },
    { regex: /(edge)\/(\d+(\.\d+){0,2})/i, name: BrowserName.Edge, versionGroup: 2 },
    { regex: /(opera)\/(\d+(\.\d+){0,2})/i, name: BrowserName.Opera, versionGroup: 2 }
];

const ENGINE_RULES: EngineRegexRule[] = [
    { regex: /(webkit)\/(\d+(\.\d+){0,2})/i, name: EngineName.WebKit, versionGroup: 2 },
    { regex: /(gecko)\/(\d+(\.\d+){0,2})/i, name: EngineName.Gecko, versionGroup: 2 },
    { regex: /(trident)\/(\d+(\.\d+){0,2})/i, name: EngineName.Trident, versionGroup: 2 },
    { regex: /(blink)\/(\d+(\.\d+){0,2})/i, name: EngineName.Blink, versionGroup: 2 }
];

export class UserAgentParser {
    public parse(ua: string): IUserAgentInfo {
        const os = this.matchNamedRule(OS_RULES, ua) as NamedMatchResult<OSName>;
        const browser = this.matchNamedRule(BROWSER_RULES, ua) as NamedMatchResult<BrowserName>;
        const engine = this.matchNamedRule(ENGINE_RULES, ua) as NamedMatchResult<EngineName>;
        const device = this.matchDeviceRule(DEVICE_RULES, ua);

        return {
            os: { name: os.name, version: this.parseVersion(os.version) },
            browser: { name: browser.name, version: this.parseVersion(browser.version) },
            engine: { name: engine.name, version: this.parseVersion(engine.version) },
            device
        };
    }

    private matchNamedRule<R extends { regex: RegExp; name: string; versionGroup?: number }>(rules: R[], ua: string): NamedMatchResult<string> {
        for (const rule of rules) {
            const match = rule.regex.exec(ua);
            if (match) {
                return {
                    name: rule.name,
                    version: rule.versionGroup ? match[rule.versionGroup].replaceAll('_', '.') : '0.0.0'
                };
            }
        }
        return { name: 'Unknown', version: '0.0.0' };
    }

    private matchDeviceRule(rules: DeviceRegexRule[], ua: string): DeviceMatchResult {
        for (const rule of rules) {
            const match = rule.regex.exec(ua);
            if (!match) continue;

            const model = rule.model ?? (rule.modelGroup ? match[rule.modelGroup] : null);

            return {
                type: rule.type,
                vendor: rule.vendor ?? null,
                model
            };
        }

        return {
            type: DeviceType.Desktop,
            vendor: null,
            model: null
        };
    }

    private parseVersion(versionStr: string): IVersionParts {
        const parts = versionStr.split(/[._]/).map((v) => Number.parseInt(v, 10) || 0);
        return { raw: versionStr, major: parts[0] ?? 0, minor: parts[1] ?? 0, patch: parts[2] ?? 0 };
    }
}
